import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  List,
  ListOrdered,
  Redo2,
  Underline,
  Undo2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ENC } from "@/constants/testIds";
import {
  insertHtmlAtCursor,
  noteContentToEditorHtml,
  plaintextToEditorHtml,
  serializeNoteRtf,
} from "@/lib/noteRtf";
import { RTF_FONT_SIZES } from "@/lib/pageDesignerRtf";
import { tryExpandSmartPhraseShortcutInRtf } from "@/lib/smartPhraseExpand";
import { cn } from "@/lib/utils";

function ToolbarButton({ title, testId, disabled, onClick, children }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      title={title}
      aria-label={title}
      data-testid={testId}
      disabled={disabled}
      className="h-7 w-7 text-slate-600"
      onMouseDown={(event) => {
        event.preventDefault();
        if (!disabled) onClick?.();
      }}
    >
      {children}
    </Button>
  );
}

function selectionIsInside(el, node) {
  if (!el || !node) return false;
  const target = node.nodeType === Node.TEXT_NODE ? node.parentNode : node;
  return Boolean(target && el.contains(target));
}

const NoteRtfEditor = forwardRef(function NoteRtfEditor(
  {
    value = "",
    onChange,
    readOnly = false,
    placeholder = "Start the clinical note…",
    testId,
    patient,
    placeholderContext,
    extraToolbar,
    className,
    surfaceClassName,
    ariaLabel = "Clinical note",
  },
  ref,
) {
  const editorRef = useRef(null);
  const lastHtml = useRef("");
  const savedRange = useRef(null);
  const expandingRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const next = noteContentToEditorHtml(value);
    if (next !== lastHtml.current && el.innerHTML !== next) {
      el.innerHTML = next;
      lastHtml.current = next;
    }
  }, [value]);

  const emitHtml = () => {
    const el = editorRef.current;
    if (!el) return;
    const clean = serializeNoteRtf(el.innerHTML || "");
    lastHtml.current = clean;
    onChangeRef.current?.(clean);
  };

  const saveSelection = () => {
    const el = editorRef.current;
    const sel = window.getSelection();
    if (!el || !sel || sel.rangeCount === 0) return;
    if (!selectionIsInside(el, sel.anchorNode)) return;
    savedRange.current = sel.getRangeAt(0).cloneRange();
  };

  const restoreSelection = () => {
    const el = editorRef.current;
    if (!el || readOnly) return;
    el.focus();
    const range = savedRange.current;
    if (!range) return;
    try {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    } catch {
      /* selection may be stale after remount */
    }
  };

  const run = (command, arg) => {
    if (readOnly) return;
    restoreSelection();
    document.execCommand(command, false, arg);
    saveSelection();
    emitHtml();
  };

  const insertHtml = (html) => {
    if (readOnly) return;
    restoreSelection();
    insertHtmlAtCursor(editorRef.current, html);
    saveSelection();
    emitHtml();
  };

  useImperativeHandle(ref, () => ({
    insertHtml,
    insertText(text) {
      insertHtml(plaintextToEditorHtml(text));
    },
    focus() {
      editorRef.current?.focus();
    },
  }));

    const onKeyDown = (event) => {
    if (readOnly || expandingRef.current) return;
    expandingRef.current = true;
    void tryExpandSmartPhraseShortcutInRtf({
      event,
      editor: editorRef.current,
      onChange: (html) => {
        lastHtml.current = html;
        onChangeRef.current?.(html);
      },
      patient,
      context: placeholderContext,
    }).finally(() => {
      expandingRef.current = false;
    });
  };

  return (
    <div className={cn("emr-note-rtf", className)}>
      {readOnly ? null : (
        <div
          className="emr-note-rtf__toolbar"
          data-testid={ENC.screenNoteRtfToolbar}
          aria-label="RTF formatting"
        >
          <span className="emr-note-rtf__label">RTF</span>
          <ToolbarButton title="Bold" testId={ENC.screenNoteRtfBold} onClick={() => run("bold")}>
            <Bold className="h-3.5 w-3.5" />
          </ToolbarButton>
          <ToolbarButton title="Italic" testId={ENC.screenNoteRtfItalic} onClick={() => run("italic")}>
            <Italic className="h-3.5 w-3.5" />
          </ToolbarButton>
          <ToolbarButton
            title="Underline"
            testId={ENC.screenNoteRtfUnderline}
            onClick={() => run("underline")}
          >
            <Underline className="h-3.5 w-3.5" />
          </ToolbarButton>
          <span className="emr-note-rtf__sep" aria-hidden="true" />
          <select
            className="emr-note-rtf__select browser-default"
            aria-label="Font size"
            defaultValue="3"
            onMouseDown={(event) => event.stopPropagation()}
            onChange={(event) => run("fontSize", event.target.value)}
          >
            {RTF_FONT_SIZES.map((size) => (
              <option key={size.value} value={size.value}>
                {size.label}
              </option>
            ))}
          </select>
          <span className="emr-note-rtf__sep" aria-hidden="true" />
          <ToolbarButton
            title="Align left"
            testId={ENC.screenNoteRtfAlignLeft}
            onClick={() => run("justifyLeft")}
          >
            <AlignLeft className="h-3.5 w-3.5" />
          </ToolbarButton>
          <ToolbarButton
            title="Align center"
            testId={ENC.screenNoteRtfAlignCenter}
            onClick={() => run("justifyCenter")}
          >
            <AlignCenter className="h-3.5 w-3.5" />
          </ToolbarButton>
          <ToolbarButton
            title="Align right"
            testId={ENC.screenNoteRtfAlignRight}
            onClick={() => run("justifyRight")}
          >
            <AlignRight className="h-3.5 w-3.5" />
          </ToolbarButton>
          <span className="emr-note-rtf__sep" aria-hidden="true" />
          <ToolbarButton
            title="Bulleted list"
            testId={ENC.screenNoteRtfBullet}
            onClick={() => run("insertUnorderedList")}
          >
            <List className="h-3.5 w-3.5" />
          </ToolbarButton>
          <ToolbarButton
            title="Numbered list"
            testId={ENC.screenNoteRtfNumber}
            onClick={() => run("insertOrderedList")}
          >
            <ListOrdered className="h-3.5 w-3.5" />
          </ToolbarButton>
          <span className="emr-note-rtf__sep" aria-hidden="true" />
          <ToolbarButton title="Undo" testId={ENC.screenNoteRtfUndo} onClick={() => run("undo")}>
            <Undo2 className="h-3.5 w-3.5" />
          </ToolbarButton>
          <ToolbarButton title="Redo" testId={ENC.screenNoteRtfRedo} onClick={() => run("redo")}>
            <Redo2 className="h-3.5 w-3.5" />
          </ToolbarButton>
          {extraToolbar ? <div className="emr-note-rtf__extra">{extraToolbar}</div> : null}
        </div>
      )}
      <div
        ref={editorRef}
        data-testid={testId}
        contentEditable={!readOnly}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-readonly={readOnly ? "true" : undefined}
        aria-label={ariaLabel}
        spellCheck
        className={cn(
          "emr-note-rtf__surface",
          readOnly ? "emr-note-rtf__surface--locked" : "",
          surfaceClassName,
        )}
        data-placeholder={readOnly ? "" : placeholder}
        onInput={() => {
          if (readOnly) return;
          saveSelection();
          emitHtml();
        }}
        onKeyUp={saveSelection}
        onMouseUp={saveSelection}
        onKeyDown={onKeyDown}
        onPaste={(event) => {
          if (readOnly) event.preventDefault();
        }}
        onCut={(event) => {
          if (readOnly) event.preventDefault();
        }}
        onDrop={(event) => {
          if (readOnly) event.preventDefault();
        }}
        onBeforeInput={(event) => {
          if (readOnly) event.preventDefault();
        }}
        onBlur={() => {
          saveSelection();
          if (!readOnly) emitHtml();
        }}
      />
    </div>
  );
});

export default NoteRtfEditor;
